import React from "react";
import PropTypes from "prop-types";
import {ACheckbox, AListItem} from "@cisco-sbg-ui/magna-react";

export default function MultiSelectCheckbox(props) {
  const {
    allValues,
    checkboxAsMenuItem,
    children,
    disabled,
    id,
    isSelectAll = false,
    listItemProps,
    selected = [],
    setSelected,
    stopPropagation = false,
    value,
    ...rest
  } = props;

  // Handles selecting/unselecting multiple values
  const handleCheckClick = (event, item) => {
    const selectedIndex = selected.indexOf(item);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, item);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);

    if (stopPropagation) {
      event.stopPropagation();
    }
  };

  // Handles select/deselect all
  const handleCheckAllClick = (event, values) => {
    const selectedValues = event.target.checked
      ? values && values.length > 0 && values.map((item) => item)
      : [];
    setSelected(selectedValues);

    if (stopPropagation) {
      event.stopPropagation();
    }
  };

  // Handles selecting multiple objects
  const compareObjIds = (event, item) => {
    let selectedValues = null;

    if (selected.some((i) => i.id === id)) {
      selectedValues = selected.filter((i) => i.id !== id);
    } else {
      selectedValues = [...selected, item];
    }
    setSelected(selectedValues);

    if (stopPropagation) {
      event.stopPropagation();
    }
  };

  const getObjById = (id) => selected.find((val) => val.id === id);

  const checkboxValue =
    typeof value === "object" && id ? getObjById(id) : value;

  const singleCheckProps = {
    ...rest,
    ...(id && checkboxAsMenuItem && {id: id}),
    value: id && !checkboxAsMenuItem ? id : value,
    disabled: disabled,
    checked: selected && selected.includes(checkboxValue)
  };

  const checkAllProps = isSelectAll && {
    ...rest,
    value: "checkAll",
    disabled: disabled,
    indeterminate: selected.length > 0 && selected.length < allValues.length,
    checked: allValues.length > 0 && selected.length === allValues.length
  };

  const checkboxProps = isSelectAll ? checkAllProps : singleCheckProps;

  const handleSingleCheckClick = (event) =>
    typeof value === "object" && id
      ? compareObjIds(event, value)
      : handleCheckClick(event, value);

  const handleClick = (event) =>
    isSelectAll
      ? handleCheckAllClick(event, allValues)
      : handleSingleCheckClick(event, value);

  return checkboxAsMenuItem ? (
    <AListItem
      onClick={(event) => !disabled && handleClick(event)}
      {...listItemProps}>
      <ACheckbox onClick={(e) => e.stopPropagation()} {...checkboxProps} />
      {children}
    </AListItem>
  ) : (
    <ACheckbox
      onClick={(event) => !disabled && handleClick(event)}
      {...checkboxProps}>
      {children}
    </ACheckbox>
  );
}

MultiSelectCheckbox.propTypes = {
  /**
   * If true, checkbox will render with click action on AListItem.
   * Use this to make entire menu item clickable vs just the checkbox input.
   *
   * @defaultValue false
   */
  checkboxAsMenuItem: PropTypes.bool,
  /**
   * Option to pass in props to AListItem when checkboxAsMenuItem={true}
   */
  listItemProps: PropTypes.object,
  /**
   * If true, the checkbox will be disabled.
   *
   * @defaultValue false
   */
  disabled: PropTypes.bool,
  /**
   * If true, "select all" functionality will be applied to the checkbox.
   *
   * @defaultValue false
   */
  isSelectAll: PropTypes.bool,
  /**
   * Array of values to be used for "select all" functionality.
   * Required when isSelectAll is true.
   */
  allValues: PropTypes.array,
  /**
   * Value to be assigned when single checkbox is checked.
   * Required when isSelectAll is false.
   */
  value: PropTypes.any,
  /**
   * If the value is an object, pass in a unique ID for the object.
   * If isSelectAll is true, this will default to: "checkAll".
   */
  id: PropTypes.string,
  /**
   * Function that sets the selected state.
   */
  setSelected: PropTypes.func.isRequired,
  /**
   * Value used for the selected state.
   */
  selected: PropTypes.array.isRequired,
  /**
   * If true, event.stopPropagation will be used when clicking the checkbox
   *
   * @defaultValue false
   */
  stopPropagation: PropTypes.bool
};
